import { FuelTypes } from './constants';
import { round } from './math';
import { Values } from './objectEnums';

export type CommencementBunkerParcel = {
	fuelGrade: Values<typeof FuelTypes>;
	quantity: number;
	pricePerTon: number;
};

export type CommencementBunkerValue = {
	fuelGrade: Values<typeof FuelTypes>;
	totalQuantity: number;
	weightedPricePerTon: number;
	numberOfParcels: number;
};

export const getWeightedPricePerTon = (
	parcels: Array<{ quantity: number; pricePerTon: number }>,
	totalQuantity: number,
) => parcels.reduce((acc, parcel) => {
	if (!parcel.pricePerTon || Number.isNaN(parcel.pricePerTon)) {
		return acc;
	}

	const quantityToUse = !parcel.quantity || Number.isNaN(parcel.quantity) ?
		0 :
		parcel.quantity;

	if (quantityToUse === 0) {
		return acc + parcel.pricePerTon;
	}

	return acc + (parcel.pricePerTon * (quantityToUse / totalQuantity));
}, 0);

export const getCommencementBunkerValues = (
	commencementBunkers: Array<CommencementBunkerParcel> | undefined,
): Array<CommencementBunkerValue> => {
	if (!commencementBunkers) {
		return [];
	}

	const parcelsByFuelGrade = new Map<Values<typeof FuelTypes>, Array<CommencementBunkerParcel>>();

	commencementBunkers.forEach((parcel) => {
		const existing = parcelsByFuelGrade.get(parcel.fuelGrade);

		if (existing == null) {
			parcelsByFuelGrade.set(parcel.fuelGrade, [parcel]);

			return;
		}

		existing.push(parcel);
	});

	return [...parcelsByFuelGrade].map(([fuelGrade, parcels]) => {
		const totalQuantity = parcels.reduce((acc, parcel) => acc + (parcel.quantity ?? 0), 0);

		return {
			fuelGrade,
			totalQuantity: round(totalQuantity, 3),
			weightedPricePerTon: round(getWeightedPricePerTon(parcels, totalQuantity), 3),
			numberOfParcels: parcels.length,
		};
	});
};
